import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge, Tabs, useToast } from "@cx/ui";
import type {
  AiAgentConfigInput,
  AiAgentRunResult,
  AiAgentRunView,
  AiAgentSuggestionPage,
  AiAgentsOverview,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { AI_AGENT_LABELS, AI_AGENTS, SCHEDULED_AGENTS, formatPct, type AiAgent } from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { SuggestionCard } from "./SuggestionCard";

/**
 * Meu Negócio › Agentes de IA (T3.7, 06-ia-agentes §3): sugestões dos agentes para revisar (nada acontece antes do
 * aceite), execuções e taxa de aceite, e a configuração (ligar o agendamento, régua do Cobrador, limites).
 */

const AGENT_HELP: Record<AiAgent, string> = {
  purchase_planner: "Todo dia às 03:17: sugere pedidos de compra por fornecedor pela previsão de vendas.",
  collector: "Todo dia às 09:00: prepara e-mails de cobrança pela régua; nada é enviado antes do seu aceite.",
  anomaly: "De hora em hora: margem negativa, desconto fora do padrão, risco de ruptura e queda de canal.",
  product_enricher:
    "Sob demanda, no Cadastro inteligente: produtos a partir de texto ou planilha do fornecedor.",
};
const RUN_STATUS: Record<AiAgentRunView["status"], [string, "info" | "success" | "danger"]> = {
  running: ["Rodando", "info"],
  done: ["Concluída", "success"],
  failed: ["Falhou", "danger"],
};

function SuggestionsTab(): ReactElement {
  const { companyId, api } = useSession();
  const [agent, setAgent] = useState<AiAgent | "">("");
  const [status, setStatus] = useState("pending");
  const [cursor, setCursor] = useState<string[]>([]);
  const q = new URLSearchParams({ status, ...(agent ? { agent } : {}) });
  if (cursor.length) q.set("cursor", cursor[cursor.length - 1]!);
  const queryKey = ["ai-agents", companyId, "suggestions"];
  const list = useQuery({
    queryKey: [...queryKey, agent, status, cursor],
    queryFn: () => api<AiAgentSuggestionPage>(`/ai/agents/suggestions?${q.toString()}`),
  });
  const rows = list.data?.data ?? [];
  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <select
          className="form-select w-auto"
          aria-label="Agente"
          value={agent}
          onChange={(e) => {
            setAgent(e.target.value as AiAgent | "");
            setCursor([]);
          }}
        >
          <option value="">Todos os agentes</option>
          {AI_AGENTS.map((a) => (
            <option key={a} value={a}>
              {AI_AGENT_LABELS[a]}
            </option>
          ))}
        </select>
        <select
          className="form-select w-auto"
          aria-label="Situação"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCursor([]);
          }}
        >
          <option value="pending">Pendentes</option>
          <option value="accepted">Aceitas</option>
          <option value="rejected">Recusadas</option>
          <option value="expired">Expiradas</option>
          <option value="all">Todas</option>
        </select>
      </div>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          title="Nenhuma sugestão"
          description="Quando um agente rodar e encontrar algo, as sugestões aparecem aqui para você revisar."
          actions={[]}
        />
      )}
      {rows.map((s) => (
        <SuggestionCard key={s.id} s={s} queryKey={queryKey} />
      ))}
      {(cursor.length > 0 || list.data?.nextCursor) && (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!cursor.length}
            onClick={() => setCursor(cursor.slice(0, -1))}
          >
            Anteriores
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!list.data?.nextCursor}
            onClick={() => setCursor([...cursor, list.data!.nextCursor!])}
          >
            Próximas
          </button>
        </div>
      )}
    </>
  );
}

function RunsTab({ ov, canRun }: { ov: AiAgentsOverview; canRun: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const run = useMutation({
    mutationFn: (agent: AiAgent) => api<AiAgentRunResult>(`/ai/agents/${agent}/run`, { method: "POST" }),
    onSuccess: (r) => {
      toast(
        "success",
        r.created
          ? `${r.created} sugestão(ões) criada(s). Revise na aba Sugestões.`
          : "Nada novo a sugerir agora.",
      );
      void qc.invalidateQueries({ queryKey: ["ai-agents", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <>
      <p className="small text-muted">
        Chamadas ao modelo hoje: {ov.usageToday.llmCalls} de {ov.usageToday.limit}. Sem modelo, os agentes
        seguem com textos padrão.
      </p>
      <div className="row g-3 mb-3">
        {ov.agents.map((a) => (
          <div className="col-md-6 col-xl-3" key={a.agent}>
            <div className="box card p-3 h-100">
              <h2 className="h6">{AI_AGENT_LABELS[a.agent]}</h2>
              <p className="small text-muted">{AGENT_HELP[a.agent]}</p>
              <div className="small">
                Últimos 30 dias: {a.stats30d.created} sugestão(ões), {a.stats30d.accepted} aceita(s)
                {a.stats30d.acceptRatePct4 !== null && <> · aceite {formatPct(a.stats30d.acceptRatePct4)}</>}
              </div>
              {a.lastRun && (
                <div className="small mt-1">
                  Última execução: {dateTime(a.lastRun.startedAt)}{" "}
                  <StatusBadge tone={RUN_STATUS[a.lastRun.status][1]}>
                    {RUN_STATUS[a.lastRun.status][0]}
                  </StatusBadge>
                </div>
              )}
              {canRun && (SCHEDULED_AGENTS as readonly string[]).includes(a.agent) && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mt-auto align-self-start"
                  style={{ marginTop: "0.75rem" }}
                  disabled={run.isPending}
                  onClick={() => run.mutate(a.agent)}
                  aria-label={`Rodar agora: ${AI_AGENT_LABELS[a.agent]}`}
                >
                  Rodar agora
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="box card">
        <div className="table-responsive">
          <table className="table align-middle mb-0" aria-label="Execuções">
            <thead>
              <tr>
                <th scope="col">Início</th>
                <th scope="col">Agente</th>
                <th scope="col">Origem</th>
                <th scope="col">Situação</th>
                <th scope="col" className="text-end">
                  Sugestões
                </th>
                <th scope="col">Modelo</th>
              </tr>
            </thead>
            <tbody>
              {ov.runs.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.startedAt)}</td>
                  <td>{AI_AGENT_LABELS[r.agent]}</td>
                  <td>{r.trigger === "cron" ? "Agendada" : "Manual"}</td>
                  <td>
                    <StatusBadge tone={RUN_STATUS[r.status][1]}>{RUN_STATUS[r.status][0]}</StatusBadge>
                    {r.error && <div className="small text-danger">{r.error}</div>}
                  </td>
                  <td className="text-end">{r.createdCount}</td>
                  <td>{r.llmUsed ? "Usado" : "Texto padrão"}</td>
                </tr>
              ))}
              {ov.runs.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted">
                    Nenhuma execução ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

type Form = {
  plannerEnabled: boolean;
  coverDays: string;
  leadTimeDays: string;
  collectorEnabled: boolean;
  steps: string;
  sentinelEnabled: boolean;
  stockoutDays: string;
  channelDrop: string;
  dailyLlmCalls: string;
};
const toForm = (c: AiAgentConfigInput): Form => ({
  plannerEnabled: c.planner.enabled,
  coverDays: String(c.planner.coverDays),
  leadTimeDays: String(c.planner.leadTimeDays),
  collectorEnabled: c.collector.enabled,
  steps: c.collector.steps.join(", "),
  sentinelEnabled: c.sentinel.enabled,
  stockoutDays: String(c.sentinel.stockoutDays),
  channelDrop: String(c.sentinel.channelDropPct4 / 10_000),
  dailyLlmCalls: String(c.dailyLlmCalls),
});
const int = (s: string) => (/^-?\d+$/.test(s.trim()) ? Number(s.trim()) : Number.NaN);

function SettingsTab({ ov, canEdit }: { ov: AiAgentsOverview; canEdit: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<Form>(() => toForm(ov.config));
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    setF(toForm(ov.config));
  }, [ov.config]);
  const save = useMutation({
    mutationFn: (body: unknown) =>
      api<AiAgentConfigInput>("/ai/agents/settings", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => {
      toast("success", "Configuração salva");
      setErrors({});
      void qc.invalidateQueries({ queryKey: ["ai-agents", companyId] });
    },
    onError: (e: Error & { fields?: Record<string, string> }) => {
      setErrors(e.fields ?? {});
      toast("danger", errorMessage(e));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      planner: {
        enabled: f.plannerEnabled,
        coverDays: int(f.coverDays),
        leadTimeDays: int(f.leadTimeDays),
        warehouseId: ov.config.planner.warehouseId,
      },
      collector: {
        enabled: f.collectorEnabled,
        steps: f.steps
          .split(/[,;\s]+/)
          .filter(Boolean)
          .map(int),
      },
      sentinel: {
        ...ov.config.sentinel,
        enabled: f.sentinelEnabled,
        stockoutDays: int(f.stockoutDays),
        channelDropPct4: int(f.channelDrop) * 10_000,
      },
      dailyLlmCalls: int(f.dailyLlmCalls),
    });
  };
  const set = (k: keyof Form) => (e: { target: { value: string; checked?: boolean; type?: string } }) =>
    setF({ ...f, [k]: e.target.type === "checkbox" ? !!e.target.checked : e.target.value });
  const num = (k: keyof Form, label: string, field: string, help?: string) => (
    <div className="col-sm-6 col-lg-3">
      <label className="form-label" htmlFor={`cfg-${k}`}>
        {label}
      </label>
      <input
        id={`cfg-${k}`}
        className={`form-control${errors[field] ? " is-invalid" : ""}`}
        inputMode="numeric"
        value={f[k] as string}
        onChange={set(k)}
        disabled={!canEdit}
      />
      {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
      {help && <div className="form-text">{help}</div>}
    </div>
  );
  const toggle = (k: keyof Form, label: string) => (
    <div className="form-check form-switch mb-2">
      <input
        id={`cfg-${k}`}
        className="form-check-input"
        type="checkbox"
        role="switch"
        checked={f[k] as boolean}
        onChange={set(k)}
        disabled={!canEdit}
      />
      <label className="form-check-label" htmlFor={`cfg-${k}`}>
        {label}
      </label>
    </div>
  );
  return (
    <form onSubmit={submit} noValidate>
      <div className="box card p-3 mb-3">
        <h2 className="h6">Planejador de compras</h2>
        {toggle("plannerEnabled", "Rodar todo dia (03:17)")}
        <div className="row g-3">
          {num(
            "coverDays",
            "Cobertura (dias)",
            "planner.coverDays",
            "Quantos dias de venda cada compra cobre.",
          )}
          {num(
            "leadTimeDays",
            "Prazo de entrega padrão (dias)",
            "planner.leadTimeDays",
            "Usado quando ainda não há recebimentos do fornecedor.",
          )}
        </div>
      </div>
      <div className="box card p-3 mb-3">
        <h2 className="h6">Cobrador</h2>
        {toggle("collectorEnabled", "Preparar cobranças todo dia (09:00)")}
        <div className="row g-3">
          <div className="col-lg-6">
            <label className="form-label" htmlFor="cfg-steps">
              Régua (dias em relação ao vencimento)
            </label>
            <input
              id="cfg-steps"
              className={`form-control${errors["collector.steps"] ? " is-invalid" : ""}`}
              value={f.steps}
              onChange={set("steps")}
              disabled={!canEdit}
            />
            {errors["collector.steps"] && <div className="invalid-feedback">{errors["collector.steps"]}</div>}
            <div className="form-text">Negativo = antes do vencimento. Ex.: -3, 0, 3, 10, 30.</div>
          </div>
        </div>
      </div>
      <div className="box card p-3 mb-3">
        <h2 className="h6">Sentinela</h2>
        {toggle("sentinelEnabled", "Vigiar de hora em hora")}
        <div className="row g-3">
          {num("stockoutDays", "Alerta de ruptura (dias de estoque)", "sentinel.stockoutDays")}
          {num("channelDrop", "Queda de canal (%)", "sentinel.channelDropPct4", "Semana atual × anterior.")}
        </div>
      </div>
      <div className="box card p-3 mb-3">
        <h2 className="h6">Limite do modelo</h2>
        <div className="row g-3">
          {num(
            "dailyLlmCalls",
            "Chamadas ao modelo por dia",
            "dailyLlmCalls",
            "Acima do limite, os agentes usam textos padrão. 0 = nunca usar o modelo.",
          )}
        </div>
      </div>
      {canEdit && (
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          Salvar configuração
        </button>
      )}
    </form>
  );
}

export function AgentsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const ov = useQuery({
    queryKey: ["ai-agents", companyId, "overview"],
    queryFn: () => api<AiAgentsOverview>("/ai/agents"),
  });
  const canEdit = can(perms, "ai_agents", "update");
  return (
    <>
      <ContentHeader title="Agentes de IA" breadcrumb={["Meu Negócio", "Agentes de IA"]} />
      <div className="alert alert-info small">
        Os agentes só sugerem. Nada é criado ou enviado antes de alguém com a permissão aceitar a sugestão.
      </div>
      <QueryStatus loading={ov.isLoading} error={ov.error} />
      {ov.data && (
        <Tabs
          tabs={[
            { key: "sugestoes", label: "Sugestões", content: <SuggestionsTab /> },
            { key: "execucoes", label: "Execuções", content: <RunsTab ov={ov.data} canRun={canEdit} /> },
            {
              key: "config",
              label: "Configurações",
              content: <SettingsTab ov={ov.data} canEdit={canEdit} />,
            },
          ]}
        />
      )}
    </>
  );
}
